import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import { companyInfo, aboutSections } from './aboutContent';

import css from './AboutUsPage.module.css';

import { ReactComponent as DownloadIcon } from '../../assets/svg/downloadIcon.svg';

const Paragraph = ({ id }) => (
  <p className={css.paragraph}>
    <FormattedMessage id={id} />
  </p>
);

const Attribution = ({ id }) => (
  <p className={css.attribution}>
    <FormattedMessage id={id} />
  </p>
);

const Box = ({ titleId, bodyId }) => (
  <div className={css.box}>
    <p className={css.boxTitle}>
      <FormattedMessage id={titleId} />
    </p>
    <Paragraph id={bodyId} />
  </div>
);

const Block = ({ block }) => {
  switch (block.type) {
    case 'p':
      return <Paragraph id={block.id} />;
    case 'attribution':
      return <Attribution id={block.id} />;
    case 'box':
      return <Box titleId={block.titleId} bodyId={block.bodyId} />;
    default:
      return null;
  }
};

const Section = ({ section }) => {
  const content = (
    <>
      <h2 className={css.sectionHeading}>
        <FormattedMessage id={section.titleId} />
      </h2>
      {section.blocks.map((block, index) => (
        <Block block={block} key={index} />
      ))}
    </>
  );

  return (
    <div className={css.section}>
      {section.gradient ? (
        <div className={css.gradientBlock}>{content}</div>
      ) : section.accentBar ? (
        <div className={css.accentBarBlock}>{content}</div>
      ) : (
        content
      )}
    </div>
  );
};

/**
 * Content-only rendering of the About Us page, meant for embedding
 * elsewhere on the site.
 */
export const AboutUsContent = () => (
  <div className={css.card}>
    {aboutSections.map((section, index) => (
      <Section section={section} key={index} />
    ))}
  </div>
);

export const AboutUsPageComponent = props => {
  const { scrollingDisabled } = props;
  const history = useHistory();
  const intl = useIntl();

  const handleDownloadPdf = () => {
    if (typeof window !== 'undefined') {
      window.print();
    }
  };

  const pageTitle = intl.formatMessage({ id: 'AboutUsPage.pageTitle' });

  return (
    <Page title={pageTitle} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.heroSection}>
            <div className={css.topRow}>
              <button className={css.backLink} onClick={() => history.goBack()} type="button">
                <FormattedMessage id="AboutUsPage.backBtn" />
              </button>
              <button className={css.downloadButton} onClick={handleDownloadPdf} type="button">
                <DownloadIcon className={css.icon} />
                <FormattedMessage id="AboutUsPage.downloadBtn" />
              </button>
            </div>

            <h1 className={css.title}>{pageTitle}</h1>
            <div className={css.subtitleRow}>
              <span>{companyInfo.name}</span>
              <span className={css.subtitleDivider}>|</span>
              <span>
                <FormattedMessage id="AboutUsPage.effectiveDate" />: {companyInfo.effectiveDate}
              </span>
            </div>
          </div>

          <div className={css.content}>
            <AboutUsContent />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const AboutUsPage = connect(mapStateToProps)(AboutUsPageComponent);

export default AboutUsPage;
